import React, { useEffect, useRef, useState } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';
import ProjectDemoModal from '../ProjectDemoModal';
import SudokuGame from '../../pages/SudokuGame';

/**
 * RealSoftwareChapter — Horizontal Scroll Showcase + Vertical Grid
 *
 * Flagship projects (mobile) get a horizontal scroll panel:
 * vertical scroll → projects travel horizontally across screen.
 *
 * Then remaining projects in a deliberate vertical layout
 * with distinct visual languages per category.
 */
const RealSoftwareChapter = ({ onSelectProject, onOpenSudokuPage }) => {
  const [activeModalProject, setActiveModalProject] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const trackRef = useRef(null);
  const annotationRef = useRef(null);

  const flagshipProjects = [
    {
      id: 'kaamkarwao',
      title: 'KaamKarwao',
      tagline: 'Gig-Economy Service Marketplace',
      desc: 'A mobile service booking platform connecting seekers with skilled workers via real-time bidding radar, Leaflet location engine, MMKV storage, and 17 admin control modules.',
      tech: ['React Native', 'Expo SDK 54', 'TanStack Query', 'Leaflet', 'MMKV'],
      videoUrl: 'https://github.com/user-attachments/assets/ae363c29-7017-44c1-b0b6-7505cb8d7bcd',
      repo: 'https://github.com/Shawaiz788/KaamKarwao',
      highlights: [
        'Full 17-Module Admin Control Panel',
        'Instant-Mount Leaflet & OpenStreetMap location search',
        'Real-time service provider bidding radar and messaging',
        'Progressive Step Auth with SecureStore session encryption'
      ],
      accent: '#d4a359',
      visual: 'map'
    },
    {
      id: 'music-streaming',
      title: 'Android Music Streaming',
      tagline: 'Native Android Audio App',
      desc: 'Native Android music player built with Java, ExoPlayer/Media3, custom PlayerManager singleton, AudD Shazam audio recognition, and SQLite caching.',
      tech: ['Android Studio', 'Java', 'ExoPlayer', 'AudD API', 'SQLite'],
      repo: 'https://github.com/Shawaiz788/android-music-streaming-starter',
      highlights: [
        'Custom PlayerManager singleton for background audio lifecycle',
        'Real-time song catalog powered by Firebase',
        'Shazam Mode audio recognition with AudD API'
      ],
      accent: '#10b981',
      visual: 'audio'
    },
    {
      id: 'rn-camera',
      title: 'React Native Camera App',
      tagline: 'Custom Camera Platform',
      desc: 'Mobile camera application with VisionCamera v4, curved radial zoom controls (1x to 5x), torch strobe capture, and Expo Media Library integration.',
      tech: ['React Native', 'VisionCamera v4', 'Expo Router', 'Media Library'],
      videoUrl: 'https://github.com/user-attachments/assets/4e0989c6-05b6-4b9f-83ee-a1f6b109d91b',
      repo: 'https://github.com/Shawaiz788/ReactNative-Camera-App',
      highlights: [
        'Curved radial zoom controls (1x → 5x)',
        'Torch strobe capture',
        'VisionCamera v4 low-latency preview'
      ],
      accent: '#38bdf8',
      visual: 'camera'
    },
    {
      id: 'firetv-podcast',
      title: 'FireTV / VegaOS',
      tagline: 'Smart TV Podcast App',
      desc: 'Cross-platform Smart TV podcast streaming application built as a Yarn Workspaces monorepo with D-pad remote focus navigation.',
      tech: ['React Native', 'Expo TV', 'FireTV', 'VegaOS', 'Monorepo'],
      videoUrl: 'https://github.com/user-attachments/assets/54e3b5af-c778-45c0-8ceb-223d5ed6cd1a',
      repo: 'https://github.com/Shawaiz788/Expo-VegaOs-Podcast-FireTV-App',
      highlights: [
        'Yarn Workspaces monorepo — FireTV, Android TV, Web',
        'D-pad remote focus navigation',
        'TV-optimized leanback UI'
      ],
      accent: '#a855f7',
      visual: 'tv'
    }
  ];

  const otherProjects = [
    {
      id: 'neem-bert-hakeem',
      title: 'Neem-Bert-Hakeem',
      category: 'AI / NLP',
      tagline: 'AI Healthcare Consultation',
      desc: 'Fine-tuned BERT transformer models for medical question-answering, symptom evaluation, and personalized herbal advice.',
      tech: ['Python', 'BERT / Transformers', 'Flask', 'NLP'],
      repo: 'https://github.com/Shawaiz788/Neem-Bert-Hakeem',
      highlights: ['Fine-tuned BERT for medical QA', 'Symptom evaluation NLP pipeline']
    },
    {
      id: 'friendsly',
      title: 'FriendsLy',
      category: 'Full-Stack',
      tagline: 'Privacy-First Social Matcher',
      desc: 'Social discovery platform enabling users to match over shared interests without continuous GPS tracking.',
      tech: ['React', 'TypeScript', 'Vite', 'Supabase'],
      repo: 'https://github.com/Shawaiz788/FriendsLy',
      highlights: ['Privacy-first proximity matching', 'Social Aura broadcasting moods']
    },
    {
      id: 'codeack',
      title: 'CodeAck',
      category: 'Full-Stack',
      tagline: 'Competitive Coding Platform',
      desc: 'Full-stack practice platform with problem directory, live code evaluation, and PostgreSQL backend.',
      tech: ['Next.js', 'NestJS', 'PostgreSQL', 'Supabase'],
      repo: 'https://github.com/AhmedNasir7/Codeack-SDA_Project.git'
    },
    {
      id: 'sudoku',
      title: 'Sudoku Game',
      category: 'x86 Assembly',
      tagline: 'Playable DOS Emulator',
      desc: 'Low-level Sudoku solver & game compiled from x86 Assembly, playable directly in browser via JS-DOS emulator.',
      tech: ['x86 Assembly', 'DOS', 'JS-DOS'],
      playable: true,
      downloadLink: '/suduko.asm',
      downloadLabel: 'Download .asm'
    },
    {
      id: 'bingo',
      title: 'Bingo Game',
      category: 'C++',
      tagline: 'Systems & Playable Web App',
      desc: 'Two-player Bingo game derived from C++ console codebase with local state, score tracking, and turn validation.',
      tech: ['C++', 'React'],
      playable: true,
      downloadLink: '/BINGO.cpp',
      downloadLabel: 'Download .cpp'
    },
    {
      id: 'bank-cpp',
      title: 'Banking System',
      category: 'C++',
      tagline: 'OOP Systems Design',
      desc: 'Object-oriented banking system with accounts, card management, transaction processing, and persistent file state.',
      tech: ['C++', 'OOP', 'File Persistence'],
      downloadLink: '/bank.cpp',
      downloadLabel: 'Download bank.cpp'
    },
    {
      id: 'fitness-cpp',
      title: 'Fitness Tracker',
      category: 'C++',
      tagline: 'Console App',
      desc: 'Console fitness application with profile management, workout guidance, progress calculations, and file storage.',
      tech: ['C++', 'OOP', 'File Persistence'],
      downloadLink: '/fitness.cpp',
      downloadLabel: 'Download fitness.cpp'
    }
  ];

  // Horizontal scroll setup
  useEffect(() => {
    if (prefersReducedMotion() || !wrapperRef.current) return;

    let ctx;
    const setup = () => {
      ctx = gsap.context(() => {
        const track = trackRef.current;
        if (!track) return;

        const maxScrollDist = track.scrollWidth - window.innerWidth;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: () => `+=${maxScrollDist + 200}`,
            pin: sceneRef.current,
            scrub: 1,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          }
        });

        tl.fromTo(annotationRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.2 },
          0
        )
        // Scroll horizontal track to center the last project card
        .to(track, {
          x: () => -maxScrollDist,
          ease: 'none',
          duration: 3.5
        }, 0.2)
        // Pin-hold buffer: Minimal hold before unpinning cleanly
        .to({}, { duration: 0.2 }, 3.7);
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  // Visual accent SVG per project type
  const renderProjectVisual = (visual, accent) => {
    switch (visual) {
      case 'map':
        return (
          <svg viewBox="0 0 200 140" width="200" height="140" fill="none" className="proj-svg">
            <rect width="200" height="140" fill={`${accent}08`} rx="8" />
            {/* Map grid */}
            {[30, 60, 90, 120, 150].map(x => (
              <line key={x} x1={x} y1="0" x2={x} y2="140" stroke={`${accent}15`} strokeWidth="1" />
            ))}
            {[30, 60, 90, 110].map(y => (
              <line key={y} x1="0" y1={y} x2="200" y2={y} stroke={`${accent}15`} strokeWidth="1" />
            ))}
            {/* Location pins */}
            <circle cx="80" cy="55" r="8" fill={`${accent}30`} stroke={accent} strokeWidth="1.5" />
            <circle cx="80" cy="55" r="3" fill={accent} />
            <circle cx="140" cy="80" r="6" fill={`${accent}20`} stroke={accent} strokeWidth="1" />
            <circle cx="140" cy="80" r="2" fill={accent} />
            <circle cx="50" cy="90" r="5" fill={`${accent}20`} stroke={accent} strokeWidth="1" />
            {/* Route line */}
            <path d="M80 55 Q110 60 140 80" stroke={accent} strokeWidth="1" strokeDasharray="4 2" opacity="0.6" />
            <text x="10" y="130" fill={`${accent}60`} fontSize="7" fontFamily="monospace">LAHORE SERVICES MAP</text>
          </svg>
        );
      case 'audio':
        return (
          <svg viewBox="0 0 200 140" width="200" height="140" fill="none" className="proj-svg">
            <rect width="200" height="140" fill={`${accent}08`} rx="8" />
            {/* Album circle */}
            <circle cx="100" cy="65" r="42" fill={`${accent}10`} stroke={`${accent}30`} strokeWidth="1.5" />
            <circle cx="100" cy="65" r="25" fill={`${accent}08`} stroke={`${accent}20`} strokeWidth="1" />
            <circle cx="100" cy="65" r="6" fill={accent} opacity="0.7" />
            {/* Waveform bars */}
            {[20, 28, 36, 44, 152, 160, 168, 176].map((x, i) => (
              <rect key={i} x={x} y={60 - i * 3 % 15} width="4" height={20 + i * 3 % 20}
                fill={accent} opacity="0.5" rx="1" />
            ))}
            <text x="10" y="130" fill={`${accent}60`} fontSize="7" fontFamily="monospace">EXOPLAYER / MEDIA3 / SHAZAM</text>
          </svg>
        );
      case 'camera':
        return (
          <svg viewBox="0 0 200 140" width="200" height="140" fill="none" className="proj-svg">
            <rect width="200" height="140" fill={`${accent}08`} rx="8" />
            {/* Camera frame */}
            <rect x="30" y="25" width="140" height="90" rx="8" stroke={`${accent}40`} strokeWidth="1.5" fill="none" />
            {/* Lens circles */}
            <circle cx="100" cy="70" r="32" stroke={accent} strokeWidth="1.5" fill={`${accent}06`} />
            <circle cx="100" cy="70" r="20" stroke={`${accent}50`} strokeWidth="1" fill={`${accent}04`} />
            <circle cx="100" cy="70" r="8" fill={`${accent}30`} />
            {/* Zoom markers */}
            {['1x','2x','3x','5x'].map((z, i) => (
              <text key={i} x={42 + i * 38} y="128" fill={i === 0 ? accent : `${accent}50`}
                fontSize="7" fontFamily="monospace" textAnchor="middle">{z}</text>
            ))}
            {/* Torch icon */}
            <circle cx="160" cy="38" r="7" fill={`${accent}15`} stroke={`${accent}40`} strokeWidth="1" />
            <text x="160" y="42" fill={accent} fontSize="9" textAnchor="middle">⚡</text>
          </svg>
        );
      case 'tv':
        return (
          <svg viewBox="0 0 200 140" width="200" height="140" fill="none" className="proj-svg">
            <rect width="200" height="140" fill={`${accent}08`} rx="8" />
            {/* TV screen */}
            <rect x="20" y="15" width="160" height="95" rx="10" stroke={`${accent}40`} strokeWidth="2" fill={`${accent}06`} />
            {/* Content cards */}
            <rect x="30" y="30" width="70" height="45" rx="4" fill={`${accent}12`} stroke={`${accent}25`} strokeWidth="1" />
            <rect x="110" y="30" width="60" height="45" rx="4" fill={`${accent}08`} stroke={`${accent}15`} strokeWidth="1" />
            {/* Focus ring on first card */}
            <rect x="28" y="28" width="74" height="49" rx="5" stroke={accent} strokeWidth="2" fill="none" />
            {/* D-pad */}
            <circle cx="100" cy="125" r="8" fill={`${accent}15`} stroke={`${accent}30`} strokeWidth="1" />
            <text x="100" y="129" fill={accent} fontSize="8" textAnchor="middle">▶</text>
            <text x="15" y="130" fill={`${accent}60`} fontSize="7" fontFamily="monospace">D-PAD FOCUS NAV</text>
          </svg>
        );
      default:
        return null;
    }
  };

  const filteredOther = otherProjects.filter(p => {
    if (activeFilter === 'all') return true;
    return p.category?.toLowerCase().includes(activeFilter.toLowerCase());
  }).filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      p.desc.toLowerCase().includes(q) ||
      p.tech.some(t => t.toLowerCase().includes(q))
    );
  });

  return (
    <div id="section-projects" className="rsw-outer">

      {/* === HORIZONTAL SCROLL SHOWCASE === */}
      <section ref={wrapperRef} className="rsw-horiz-wrapper">
        <div ref={sceneRef} className="rsw-horiz-scene">

          <div ref={annotationRef} className="rsw-annotation font-mono">
            CH. 08 &nbsp;// REAL SOFTWARE
          </div>

          <div className="rsw-chapter-label font-display">
            Projects
          </div>

          {/* Horizontal track */}
          <div ref={trackRef} className="rsw-track">
            {/* === CHAPTER 08 DEDICATED TITLE CARD === */}
            <div className="rsw-card rsw-title-card">
              <div className="ch08-card-content">
                <div className="ch08-tag font-mono">
                  <span className="ch08-dot" />
                  CHAPTER 08 &nbsp;//&nbsp; REAL SOFTWARE
                </div>
                <h2 className="ch08-headline font-display">
                  Real Software.<br />
                  <span className="ch08-accent-text">Production Showcase.</span>
                </h2>
                <p className="ch08-desc font-body">
                  Full-scale mobile applications built for real users — featuring gig-economy service marketplaces, native audio streaming players, Smart TV TVOS monorepos, and custom camera platforms.
                </p>
                <div className="ch08-cue font-mono">
                  <span>SWIPE / SCROLL TO EXPLORE</span>
                  <span className="ch08-arrow">→</span>
                </div>
              </div>
            </div>

            {flagshipProjects.map((proj, i) => (
              <div key={proj.id} className="rsw-card" style={{ '--card-accent': proj.accent }}>
                {/* Card number */}
                <div className="rsw-card-num font-mono">{String(i + 1).padStart(2, '0')} / {flagshipProjects.length}</div>

                {/* Visual */}
                <div className="rsw-card-visual">
                  {renderProjectVisual(proj.visual, proj.accent)}
                </div>

                {/* Content */}
                <div className="rsw-card-content">
                  <div className="rsw-card-tagline font-mono" style={{ color: proj.accent }}>
                    {proj.tagline}
                  </div>
                  <h2 className="rsw-card-title font-heading">{proj.title}</h2>
                  <p className="rsw-card-desc">{proj.desc}</p>

                  <div className="rsw-card-highlights">
                    {proj.highlights.map((h, j) => (
                      <div key={j} className="rsw-highlight font-mono">
                        <span className="rsw-highlight-dot" style={{ background: proj.accent }} />
                        {h}
                      </div>
                    ))}
                  </div>

                  <div className="rsw-card-tech">
                    {proj.tech.map(t => (
                      <span key={t} className="rsw-tech-pill font-mono">{t}</span>
                    ))}
                  </div>

                  <div className="rsw-card-actions">
                    {proj.videoUrl && (
                      <button
                        className="rsw-btn-demo"
                        style={{ borderColor: proj.accent, color: proj.accent }}
                        onClick={() => setActiveModalProject(proj)}
                      >
                        View Demo
                      </button>
                    )}
                    {proj.repo && (
                      <a href={proj.repo} target="_blank" rel="noreferrer" className="rsw-btn-repo">
                        GitHub →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll indicator */}
          <div className="rsw-scroll-indicator font-mono">
            ← SCROLL TO EXPLORE →
          </div>
        </div>
      </section>

      {/* === INTERACTIVE PLAYABLE SUDOKU GAME SHOWCASE === */}
      <section className="rsw-sudoku-section">
        <div className="rsw-sudoku-container">
          <SudokuGame />
        </div>
      </section>

      {/* === REMAINING PROJECTS — vertical editorial layout === */}
      <section className="rsw-rest-section">
        <div className="rsw-rest-header">
          <div className="rsw-rest-tag font-mono">MORE PROJECTS</div>
          <div className="rsw-rest-controls">
            {/* Search */}
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rsw-search font-mono"
            />
            {/* Filter chips */}
            {['all', 'AI / NLP', 'Full-Stack', 'C++', 'x86 Assembly'].map(f => (
              <button
                key={f}
                className={`rsw-filter-chip font-mono${activeFilter === f ? ' active' : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f === 'all' ? 'ALL' : f}
              </button>
            ))}
          </div>
        </div>

        <div className="rsw-rest-grid">
          {filteredOther.map(proj => (
            <div key={proj.id} className={`rsw-rest-card${proj.category?.includes('Assembly') || proj.category?.includes('C++') ? ' rsw-rest-card--systems' : ''}`}>
              <div className="rsw-rest-category font-mono">{proj.category}</div>
              <h3 className="rsw-rest-title">{proj.title}</h3>
              <div className="rsw-rest-tagline font-mono">{proj.tagline}</div>
              <p className="rsw-rest-desc">{proj.desc}</p>
              {proj.highlights && (
                <ul className="rsw-rest-highlights">
                  {proj.highlights.map((h, i) => <li key={i} className="font-mono">{h}</li>)}
                </ul>
              )}
              <div className="rsw-rest-tech">
                {proj.tech.map(t => <span key={t} className="rsw-rest-pill font-mono">{t}</span>)}
              </div>
              <div className="rsw-rest-actions">
                {proj.playable && (
                  <button
                    className="rsw-rest-btn rsw-rest-btn--play"
                    onClick={() => onSelectProject && onSelectProject(proj.id)}
                  >
                    ▶ Play in Browser
                  </button>
                )}
                {proj.id === 'sudoku' && onOpenSudokuPage && (
                  <button
                    className="rsw-rest-btn font-mono"
                    onClick={onOpenSudokuPage}
                    title="Open dedicated shareable /sudoku page"
                  >
                    🔗 Direct Page (/sudoku)
                  </button>
                )}
                {proj.repo && (
                  <a href={proj.repo} target="_blank" rel="noreferrer" className="rsw-rest-btn">
                    GitHub →
                  </a>
                )}
                {proj.downloadLink && (
                  <a href={proj.downloadLink} download={proj.downloadLabel} className="rsw-rest-btn">
                    ↓ {proj.downloadLabel}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Demo modal */}
      {activeModalProject && (
        <ProjectDemoModal
          project={activeModalProject}
          onClose={() => setActiveModalProject(null)}
        />
      )}

      <style>{`
        .rsw-outer {
          background: #080a0f;
        }

        /* === HORIZONTAL SECTION === */
        .rsw-horiz-wrapper {
          position: relative;
          height: auto;
          background: #080a0f;
        }

        .rsw-horiz-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .rsw-annotation {
          position: absolute;
          top: 5vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.5);
          z-index: 10;
          will-change: opacity;
        }

        .rsw-chapter-label {
          position: absolute;
          top: 5vh;
          right: 5vw;
          font-size: clamp(0.75rem, 2vw, 1.1rem);
          font-weight: 700;
          color: rgba(243, 244, 246, 0.15);
          letter-spacing: 0.1em;
          z-index: 10;
        }

        /* The horizontal track */
        .rsw-track {
          display: flex;
          align-items: center;
          gap: 0;
          height: 100vh;
          will-change: transform;
          padding: 0 35vw 0 5vw;
        }

        /* Individual project card */
        .rsw-card {
          flex-shrink: 0;
          width: min(85vw, 760px);
          min-height: 520px;
          max-height: 80vh;
          margin-right: 5vw;
          background: rgba(14, 18, 27, 0.92);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 2.25rem 2.5rem;
          display: flex;
          gap: 2.25rem;
          align-items: center;
          position: relative;
          overflow: hidden;
          transition: border-color 0.3s, box-shadow 0.3s;
        }

        .rsw-title-card {
          background: rgba(12, 17, 28, 0.96);
          border: 1px solid rgba(56, 189, 248, 0.25);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);
        }

        .ch08-card-content {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          max-width: 620px;
        }

        .ch08-tag {
          color: #38bdf8;
          font-size: 0.75rem;
          letter-spacing: 0.22em;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(56, 189, 248, 0.08);
          border: 1px solid rgba(56, 189, 248, 0.25);
          padding: 0.4rem 1rem;
          border-radius: 100px;
          width: fit-content;
        }

        .ch08-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #38bdf8;
          box-shadow: 0 0 10px #38bdf8;
        }

        .ch08-headline {
          font-size: clamp(2.4rem, 5vw, 3.8rem);
          line-height: 1.15;
          color: #ffffff;
          margin: 0;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .ch08-accent-text {
          color: #38bdf8;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 500;
        }

        .ch08-desc {
          color: rgba(209, 213, 219, 0.85);
          font-size: clamp(0.95rem, 1.2vw, 1.05rem);
          line-height: 1.65;
          margin: 0;
        }

        .ch08-cue {
          margin-top: 0.5rem;
          font-size: 0.72rem;
          letter-spacing: 0.18em;
          color: #d4a359;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-weight: 600;
        }

        .ch08-arrow {
          font-size: 1.1rem;
          animation: arrowPulse 1.8s infinite ease-in-out;
        }

        @keyframes arrowPulse {
          0%, 100% { transform: translateX(0); opacity: 0.6; }
          50% { transform: translateX(6px); opacity: 1; }
        }

        .rsw-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(var(--card-accent-rgb, 212,163,89), 0.04) 0%, transparent 60%);
          border-radius: 16px;
          pointer-events: none;
        }

        .rsw-card:hover {
          border-color: rgba(212, 163, 89, 0.3);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }

        .rsw-card-num {
          position: absolute;
          top: 1.5rem;
          right: 2rem;
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          color: rgba(156, 163, 175, 0.5);
        }

        .rsw-card-visual {
          flex-shrink: 0;
          opacity: 0.9;
        }

        .rsw-card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          min-width: 0;
          height: 100%;
          justify-content: center;
        }

        .rsw-card-tagline {
          font-size: 0.65rem;
          letter-spacing: 0.16em;
          font-weight: 600;
          text-transform: uppercase;
        }

        .rsw-card-title {
          font-size: clamp(1.4rem, 2.5vw, 2.1rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #f3f4f6;
          line-height: 1.2;
          overflow-wrap: break-word;
          word-break: break-word;
        }

        .rsw-card-desc {
          font-size: 0.85rem;
          color: rgba(156, 163, 175, 0.85);
          line-height: 1.6;
          margin-bottom: 0.2rem;
        }

        .rsw-card-highlights {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          margin-bottom: 0.2rem;
        }

        .rsw-highlight {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          font-size: 0.72rem;
          letter-spacing: 0.03em;
          color: rgba(180, 187, 198, 0.8);
          line-height: 1.45;
        }

        .rsw-highlight-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          margin-top: 0.38rem;
          flex-shrink: 0;
        }

        .rsw-card-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem;
          margin-bottom: 0.4rem;
        }

        .rsw-tech-pill {
          font-size: 0.62rem;
          letter-spacing: 0.06em;
          color: rgba(56, 189, 248, 0.85);
          background: rgba(56, 189, 248, 0.08);
          border: 1px solid rgba(56, 189, 248, 0.2);
          padding: 0.22rem 0.55rem;
          border-radius: 4px;
        }

        .rsw-card-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-top: 0.4rem;
        }

        .rsw-btn-demo {
          padding: 0.5rem 1.1rem;
          border-radius: 6px;
          background: transparent;
          border: 1px solid;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 600;
          font-family: inherit;
          transition: all 0.2s;
        }

        .rsw-btn-demo:hover {
          opacity: 0.8;
        }

        .rsw-btn-repo {
          padding: 0.5rem 1.1rem;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(156, 163, 175, 0.8);
          font-size: 0.75rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }

        .rsw-btn-repo:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .rsw-scroll-indicator {
          position: absolute;
          bottom: 4vh;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.6rem;
          letter-spacing: 0.22em;
          color: rgba(107, 114, 128, 0.4);
          z-index: 10;
        }

        /* === REST SECTION === */
        .rsw-rest-section {
          background: #0e121b;
          padding: clamp(4rem, 8vw, 7rem) clamp(1.5rem, 6vw, 5rem);
        }

        .rsw-rest-header {
          max-width: 1100px;
          margin: 0 auto 3rem auto;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .rsw-rest-tag {
          font-size: 0.65rem;
          letter-spacing: 0.22em;
          color: rgba(212, 163, 89, 0.6);
        }

        .rsw-rest-controls {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .rsw-search {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          padding: 0.45rem 0.9rem;
          font-size: 0.75rem;
          color: rgba(156, 163, 175, 0.9);
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.04em;
          outline: none;
          width: 200px;
          transition: border-color 0.2s;
        }

        .rsw-search:focus {
          border-color: rgba(212, 163, 89, 0.35);
        }

        .rsw-filter-chip {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          color: rgba(107, 114, 128, 0.8);
          padding: 0.35rem 0.75rem;
          border-radius: 5px;
          cursor: pointer;
          font-size: 0.62rem;
          letter-spacing: 0.1em;
          transition: all 0.2s;
        }

        .rsw-filter-chip.active {
          background: rgba(212, 163, 89, 0.1);
          border-color: rgba(212, 163, 89, 0.3);
          color: var(--accent-gold);
        }

        .rsw-rest-grid {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .rsw-rest-card {
          background: rgba(16, 21, 31, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          transition: border-color 0.25s, box-shadow 0.25s;
        }

        .rsw-rest-card:hover {
          border-color: rgba(255, 255, 255, 0.12);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        /* Systems cards get monospace visual treatment */
        .rsw-rest-card--systems {
          background: rgba(11, 14, 20, 0.9);
          border-color: rgba(56, 189, 248, 0.08);
          font-family: 'JetBrains Mono', monospace;
        }

        .rsw-rest-card--systems:hover {
          border-color: rgba(56, 189, 248, 0.2);
        }

        .rsw-rest-category {
          font-size: 0.6rem;
          letter-spacing: 0.16em;
          color: rgba(212, 163, 89, 0.7);
        }

        .rsw-rest-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #f3f4f6;
          letter-spacing: -0.01em;
        }

        .rsw-rest-tagline {
          font-size: 0.65rem;
          letter-spacing: 0.1em;
          color: rgba(107, 114, 128, 0.7);
        }

        .rsw-rest-desc {
          font-size: 0.85rem;
          color: rgba(156, 163, 175, 0.7);
          line-height: 1.6;
        }

        .rsw-rest-highlights {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .rsw-rest-highlights li {
          font-size: 0.68rem;
          letter-spacing: 0.04em;
          color: rgba(107, 114, 128, 0.7);
          padding-left: 0.75rem;
          position: relative;
        }

        .rsw-rest-highlights li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: rgba(212, 163, 89, 0.5);
        }

        .rsw-rest-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.3rem;
          margin-top: auto;
        }

        .rsw-rest-pill {
          font-size: 0.58rem;
          letter-spacing: 0.06em;
          color: rgba(56, 189, 248, 0.6);
          background: rgba(56, 189, 248, 0.05);
          border: 1px solid rgba(56, 189, 248, 0.12);
          padding: 0.18rem 0.45rem;
          border-radius: 3px;
        }

        .rsw-rest-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .rsw-rest-btn {
          padding: 0.4rem 0.8rem;
          border-radius: 5px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          color: rgba(156, 163, 175, 0.8);
          font-size: 0.7rem;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.06em;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rsw-rest-btn:hover {
          background: rgba(255, 255, 255, 0.09);
        }

        .rsw-rest-btn--play {
          background: rgba(212, 163, 89, 0.08);
          border-color: rgba(212, 163, 89, 0.25);
          color: rgba(212, 163, 89, 0.9);
        }

        .rsw-rest-btn--play:hover {
          background: rgba(212, 163, 89, 0.14);
        }

        @media (max-width: 768px) {
          .rsw-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          .rsw-chapter-label {
            top: calc(56px + 12px);
            right: 5vw;
            font-size: 0.72rem;
          }

          .rsw-track {
            padding: 0 15vw 0 6vw;
          }

          /* Compact, beautifully proportioned project cards for mobile screen */
          .rsw-card {
            flex-direction: column;
            width: min(88vw, 360px);
            height: auto;
            min-height: auto;
            max-height: calc(100vh - 150px);
            padding: 1.1rem 1.25rem;
            gap: 0.45rem;
            margin-right: 4vw;
            border-radius: 14px;
            justify-content: center;
          }

          .rsw-card-num {
            top: 0.75rem;
            right: 1rem;
            font-size: 0.56rem;
          }

          .rsw-card-visual {
            margin: 0 auto;
            display: flex;
            justify-content: center;
          }

          .proj-svg {
            height: 75px !important;
            width: auto !important;
            max-width: 140px;
          }

          .rsw-card-content {
            gap: 0.35rem;
            justify-content: flex-start;
          }

          .rsw-card-tagline {
            font-size: 0.54rem;
            letter-spacing: 0.1em;
          }

          .rsw-card-title {
            font-size: clamp(1.05rem, 4.5vw, 1.3rem);
            line-height: 1.15;
            margin: 0;
          }

          .rsw-card-desc {
            font-size: 0.74rem;
            line-height: 1.38;
            margin-bottom: 0.1rem;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .rsw-card-highlights {
            gap: 0.2rem;
            margin-bottom: 0.1rem;
          }

          /* Show top 2 highlights on mobile to keep card height compact */
          .rsw-card-highlights .rsw-highlight:nth-child(n+3) {
            display: none;
          }

          .rsw-highlight {
            font-size: 0.6rem;
            line-height: 1.3;
          }

          .rsw-highlight-dot {
            width: 4px;
            height: 4px;
            margin-top: 0.25rem;
          }

          .rsw-card-tech {
            gap: 0.25rem;
            margin-bottom: 0.25rem;
          }

          .rsw-tech-pill {
            font-size: 0.52rem;
            padding: 0.15rem 0.4rem;
          }

          .rsw-card-actions {
            gap: 0.5rem;
            margin-top: 0.2rem;
          }

          .rsw-btn-demo,
          .rsw-btn-repo {
            padding: 0.35rem 0.75rem;
            font-size: 0.68rem;
          }

          /* Dedicated title card styling on mobile */
          .rsw-title-card {
            padding: 1.25rem 1.25rem;
          }

          .ch08-card-content {
            gap: 0.75rem;
          }

          .ch08-tag {
            font-size: 0.62rem;
            padding: 0.25rem 0.65rem;
          }

          .ch08-headline {
            font-size: clamp(1.45rem, 6vw, 2rem);
            line-height: 1.15;
          }

          .ch08-desc {
            font-size: 0.76rem;
            line-height: 1.45;
          }

          .ch08-cue {
            font-size: 0.6rem;
            letter-spacing: 0.12em;
          }

          .rsw-scroll-indicator {
            bottom: calc(64px + 8px);
            font-size: 0.55rem;
            letter-spacing: 0.16em;
          }

          /* Rest of projects grid on mobile */
          .rsw-rest-section {
            padding: 3rem 1rem 4rem 1rem;
          }

          .rsw-rest-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }

          .rsw-rest-card {
            padding: 1.25rem;
          }

          .rsw-rest-controls {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }

          .rsw-search {
            width: 100%;
          }

          .rsw-sudoku-section {
            padding: 3.5rem 1rem;
          }

          .rsw-sudoku-title {
            font-size: clamp(1.6rem, 7vw, 2.4rem);
          }
        }

        /* Sudoku Section */
        .rsw-sudoku-section {
          padding: 6rem 1.5rem;
          background: #080a0f;
          border-top: 1px solid rgba(212, 163, 89, 0.15);
          position: relative;
        }

        .rsw-sudoku-container {
          max-width: 1000px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2.5rem;
        }

        .rsw-sudoku-header {
          text-align: center;
          max-width: 680px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .rsw-sudoku-tag {
          color: #d4a359;
          font-size: 0.75rem;
          letter-spacing: 0.2em;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.25);
          padding: 0.4rem 1rem;
          border-radius: 100px;
        }

        .rsw-sudoku-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a359;
          box-shadow: 0 0 10px #d4a359;
        }

        .rsw-sudoku-title {
          font-size: clamp(2.2rem, 5vw, 3.6rem);
          color: #ffffff;
          margin: 0;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .rsw-sudoku-accent {
          color: #d4a359;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 500;
        }

        .rsw-sudoku-desc {
          color: rgba(209, 213, 219, 0.85);
          font-size: clamp(0.9rem, 1.2vw, 1.05rem);
          line-height: 1.6;
          margin: 0;
        }

        .rsw-sudoku-frame {
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          background: #0e121b;
          border: 1px solid rgba(212, 163, 89, 0.2);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.7);
        }
      `}</style>
    </div>
  );
};

export default RealSoftwareChapter;
